/* eslint-disable max-len */
import type { Island } from '../../../islands/island.class';
import type { Panel } from '../../../panels/panel.class';
import type { PanelItem } from '../../../panel-items/panel-item.class';

import { ActionRuleParams } from '../../types';
import { formatValidation, getIslandLetter } from '../island-validation-format';
import { getSurroundFinishProfile, nearestSixteenth } from '../../../../helpers';
import { libLang } from '../../../../lang';
import { NotificationType } from '../../../notifications/enums';

const COMBUSTIBLE_CLEARANCE = libLang.validations.island.panel.combustibleClearance;

// tmpl-custom-26 (non-MOKS) states this as the floor for ANY surface behind the appliance --
// "7" ... to any non-combustible surface and 15" ... to any combustible surface" -- so under 7"
// is a violation no matter what is actually back there, while 7"-15" turns on a combustible /
// non-combustible classification this domain model has no field for anywhere (see CLAUDE.md).
// Only the unconditional floor is enforced below; the 15" combustible uplift is deliberately
// NOT checked, and MOKS's documented 24" figure is not checked at all -- see CLAUDE.md.
const MIN_CLEARANCE_BEHIND_NON_MOKS = 7;

type ClearanceViolation = {
	panelId: string;
	itemRefId: string;
	itemLabel: string;
	islandDescription: string;
	panelLabel: string;
	clearance: number;
};

type CoyoteGrillPlacement = {
	panelItem: PanelItem;
	panel: Panel;
};

// Coyote identification reuses the existing idiom from non-coyote-drop-in/non-coyote-side-panel
// (item.vendor.displayValue === 'Coyote') combined with ComponentCategory.isGrillOrSideBurner --
// "grills & cooking appliances" is exactly that pair of categories. This is deliberately broader
// than grillJacketSkuMap, which only covers grills with a mapped insulated jacket sku and skips
// 50" hybrids/electric units -- the clearance requirement is not limited to jacketed grills.
const findCoyoteGrillsOnFrontOrBackPanel = (island: Island): Array<CoyoteGrillPlacement> => {
	const placements: Array<CoyoteGrillPlacement> = [];

	island.panels.children.forEach((panel) => {
		// Depth budget below (island depth less front+back panel thickness) only holds along the
		// front/back axis -- a grill mounted on a side panel would need the island's width instead,
		// which this validation does not attempt. See CLAUDE.md "Known limitations".
		if (!panel.isFrontPanel && !panel.isBackPanel) return;

		panel.panelItemGroups.children.forEach((group) => {
			group.items.children.forEach((panelItem) => {
				const { item } = panelItem;
				const isCoyoteGrillOrCookingAppliance = item.vendor?.displayValue === 'Coyote' && (item.category?.isGrillOrSideBurner ?? false);
				if (isCoyoteGrillOrCookingAppliance) placements.push({ panelItem, panel });
			});
		});
	});

	return placements;
};

/**
 * Flags a Coyote grill or cooking appliance (grills & side burners) left with less than the
 * manufacturer's minimum clearance behind it within the island's own cabinet depth.
 *
 * Non-MOKS only. MOKS islands are deliberately skipped -- the MOKS template's 24" figure governs
 * clearance to combustible material OUTSIDE the kitchen structure (confirmed by the numbers: a
 * standard MOKS island is only 21.25"-28.5" deep overall, so 24" of INTERNAL clearance behind an
 * appliance is physically impossible on the islands this system builds). This domain model has
 * no representation of what, if anything, sits behind the island in the customer's yard, so that
 * figure cannot be checked here. See CLAUDE.md for the full reasoning.
 */
export const combustibleClearance = (params: ActionRuleParams): void => {
	const { refMap } = params;
	const { islands } = refMap;
	const { notifications } = refMap.kitchen.design;

	notifications.purgeReferences(COMBUSTIBLE_CLEARANCE);

	if (!islands) return;

	const violations: Array<ClearanceViolation> = [];

	islands.children.forEach((island) => {
		// Corner islands have two back panels (back-left/back-right) and no single back-panel
		// thickness -- out of scope for this simple front/back cabinet-depth budget.
		if (island.isCornerIsland) return;

		const isMok = getSurroundFinishProfile(island.surround?.material)?.key === 'mok';
		if (isMok) return;

		const frontPanel = island.panels.children.find((panel) => panel.isFrontPanel);
		const backPanel = island.panels.children.find((panel) => panel.isBackPanel);
		if (!frontPanel || !backPanel) return;

		const cabinetDepth = island.islandSize.depth - frontPanel.thickness - backPanel.thickness;

		findCoyoteGrillsOnFrontOrBackPanel(island).forEach(({ panelItem, panel }) => {
			const cutoutDepth = panelItem.cutout?.depth ?? 0;
			const clearance = nearestSixteenth(cabinetDepth - cutoutDepth);

			if (clearance < MIN_CLEARANCE_BEHIND_NON_MOKS) {
				violations.push({
					panelId: panel.id,
					itemRefId: panelItem.id,
					itemLabel: panelItem.item.displayText || panelItem.item.sku,
					islandDescription: island.description,
					panelLabel: panel.label,
					clearance
				});
			}
		});
	});

	violations.forEach((violation) => {
		const island = islands.children.find((isl) => isl.panels.children.some((panel) => panel.id === violation.panelId));

		notifications.add({
			refId: violation.panelId,
			itemRefId: violation.itemRefId,
			validationCode: COMBUSTIBLE_CLEARANCE,
			description: formatValidation({
				letter: island ? getIslandLetter(island) : '',
				islandDescription: violation.islandDescription,
				location: violation.panelLabel,
				issue: `${violation.itemLabel || 'Item'} needs at least ${MIN_CLEARANCE_BEHIND_NON_MOKS}" clearance behind it to a non-combustible surface — current ${violation.clearance}". If the surface behind this island is combustible, Coyote requires 15" instead; verify on site.`
			}),
			type: NotificationType.Warn
		});
	});
};
